<template>
  <donut-form-input
    :for="props.for"
    :name="props.name"
    :validators="props.validators"
  >
    <donut-accordion-radio
      v-model="selectedOption"
      :options="options"
      @update:modelValue="handleOptionChange"
    >
      <template #monetary>
        <div class="f-v f-gap-8">
          <div class="t gray">What is the monetary value of the reward?</div>
          <div class="f-h f-gap-8">
            <div class="f-h">
              <label class="p-t12">$</label>
              <input
                v-model="monetaryValue"
                class="w-max-100"
                type="number"
                min="0"
                step="0.01"
                placeholder="10"
              >
              <label
                v-emoji="pointsPreviewText"
                class="p-t12"
              />
            </div>
          </div>
          <div
            v-emoji="exchangeRateText"
            class="t gray"
          />
        </div>
      </template>
      <template #nonMonetary>
        <div class="f-h">
          <div>
            <input
              v-model="pointsCost"
              class="w-max-100"
              type="number"
              min="0"
              placeholder="-"
            >
            <label
              class="t gray"
              :for="props.name"
            >
              Number of points required to redeem
            </label>
          </div>
          <i
            v-tooltip="rewardPointsTooltip"
            class="mi md-18 gray p-v8"
          >info</i>
        </div>
      </template>
    </donut-accordion-radio>
  </donut-form-input>
</template>

<script setup>
  import { computed, ref, watch, inject, onBeforeMount } from 'vue';
  import { useStore } from 'vuex';

  const $i18n = inject('$i18n');

  const props = defineProps({
    for: {
      type: [Object, Array, String, Number, Boolean, Date],
      default: null,
    },
    isMonetary: {
      type: Boolean,
      default: true,
    },
    name: {
      type: String,
      required: true,
    },
    validators: {
      type: Array,
      default: () => [],
    },
    dollarsPerPoint: {
      type: Number,
      required: true,
    },
    pointsConfig: {
      type: Object,
      required: true,
    },
  });

  const { state, commit } = useStore();

  // Reactive state
  const selectedOption = ref(null);
  const monetaryValue = ref(null);
  const pointsCost = ref(null);

  const options = [
    {
      value: 'monetary',
      title: 'Reward has monetary value',
    },
    {
      value: 'nonMonetary',
      title: 'Reward has non-monetary value',
    },
  ];

  // Display text computeds
  const pointsPreviewText = computed(() => `= ${pointsCost.value || 100} ${props.pointsConfig.points_name_plural} :${props.pointsConfig.emoji}:`);

  const exchangeRateText = computed(() => `Exchange rate 1 ${props.pointsConfig.points_name} = ${Math.round(props.dollarsPerPoint * 100)}¢ :${props.pointsConfig.emoji}:`);

  const rewardPointsTooltip = computed(() => `The number of ${props.pointsConfig.points_name_plural} required to redeem the reward.`);

  // Utility functions
  const formatMonetaryValue = (value) => {
    if (value === null) return null;
    if (value % 1 !== 0) return value.toFixed(2);
    return String(value);
  };

  const convertMonetaryToPoints = (monetary) => (monetary > 0 ? Math.ceil(monetary / props.dollarsPerPoint) : null);

  const convertPointsToMonetary = (points) => (points > 0 ? points * props.dollarsPerPoint : null);

  const updateStore = (key, value) => {
    commit('rewardsListings/updateListingBeingEdited', { key, value });
  };

  // Event handlers
  const handleOptionChange = (option) => {
    selectedOption.value = option;
    updateStore('is_monetary', option.value === 'monetary');
  };

  // Watchers for bidirectional sync
  let isUpdatingFromMonetary = false;
  let isUpdatingFromPoints = false;

  watch(monetaryValue, (newMonetaryValue) => {
    if (isUpdatingFromPoints) return;

    isUpdatingFromMonetary = true;
    const newPoints = convertMonetaryToPoints(newMonetaryValue);
    if (newPoints !== pointsCost.value) {
      pointsCost.value = newPoints;
    }
    isUpdatingFromMonetary = false;
  });

  watch(pointsCost, (newPointsCost) => {
    if (isUpdatingFromMonetary) return;

    isUpdatingFromPoints = true;
    const newMonetary = convertPointsToMonetary(newPointsCost);
    const formattedValue = formatMonetaryValue(newMonetary);
    const currentFormatted = monetaryValue.value === null ? null : String(monetaryValue.value);

    if (currentFormatted !== formattedValue) {
      monetaryValue.value = formattedValue;
    }

    updateStore('points_cost', newPointsCost);
    isUpdatingFromPoints = false;
  });

  // Initialization
  onBeforeMount(() => {
    selectedOption.value = props.isMonetary ? options[0] : options[1];
    pointsCost.value = props.for;
  });
</script>
